/** @type {Map<string, (el: HTMLElement) => void>} */
const mounts = new Map();

/** @type {Map<string, (el: HTMLElement) => void>} */
const unmounts = new Map();

/** @type {MutationObserver | null} */
let observer = null;

/**
 * Run callback for all existing and future elements matching selector.
 * When called without a callback, returns a deferred proxy — all method calls
 * are recorded and replayed on each matched element on mount.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {(this: HTMLElement, el: HTMLElement, refs: Refs) => void} [callback] - Called for each mount
 * @returns {HTMLElement}
 */
export function mount(selector, callback) {
    if (!callback) return deferredProxy(cb => mount(selector, cb));

    const wrapped = el => callback.call(el, el, buildRefs(el));
    mounts.set(selector, wrapped);
    document.querySelectorAll(selector).forEach(wrapped);
    initObserver();
}

/**
 * Run callback whenever an element matching selector is removed from DOM.
 * When called without a callback, returns a deferred proxy — all method calls
 * are recorded and replayed on each matched element on unmount.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {(this: HTMLElement, el: HTMLElement, refs: Refs) => void} [callback] - Called for each unmount
 * @returns {HTMLElement}
 */
export function unmount(selector, callback) {
    if (!callback) return deferredProxy(cb => unmount(selector, cb));

    unmounts.set(selector, el => callback.call(el, el, buildRefs(el)));
    initObserver();
}

/**
 * Define a small component with mount/unmount lifecycle hooks.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {{ mount?: (this: HTMLElement, el: HTMLElement, refs: Refs) => void, unmount?: (this: HTMLElement, el: HTMLElement, refs: Refs) => void }} def
 */
export function component(selector, def) {
    if (typeof def === 'function') {
        mount(selector, function () {
            const instance = new def(this);
            this.__componentInstance = instance;
            if (typeof instance.mount === 'function') {
                instance.mount();
            }
        });
        unmount(selector, function () {
            const instance = this.__componentInstance;
            if (instance && typeof instance.unmount === 'function') {
                instance.unmount();
            }
            delete this.__componentInstance;
        });
        return;
    }

    if (def.mount) mount(selector, def.mount);
    if (def.unmount) unmount(selector, def.unmount);
}

/**
 * @typedef {Object<string, HTMLElement> & {all: Object<string, NodeListOf<HTMLElement>>}} Refs
 */

/**
 * Builds a refs proxy scoped to `el`:
 *   refs.foo      → el.querySelector('[data-ref="foo"]')
 *   refs.all.foo  → el.querySelectorAll('[data-ref="foo"]')
 *
 * @private
 * @param {HTMLElement} el
 * @returns {Refs}
 */
function buildRefs(el) {
    const all = new Proxy({}, {
        get: (_, name) => typeof name === 'string' ? el.querySelectorAll(`[data-ref="${name}"]`) : undefined,
    });
    return new Proxy({}, {
        get: (_, name) => {
            if (name === 'all') return all;
            return typeof name === 'string' ? el.querySelector(`[data-ref="${name}"]`) : undefined;
        },
    });
}

/**
 * Returns a deferred jQuery-like proxy — all method calls are recorded
 * and replayed on $(el) when the element is mounted.
 *
 * @param {string} selector - CSS selector to match elements
 * @returns {JQuery}
 */
export function $mount(selector) {
    return deferredProxy(cb => mount(selector, cb), el => $(el));
}

/**
 * Builds a proxy that records every method call and replays them on each
 * element matched by `register`. `wrap` lets callers re-target the replay
 * (e.g. through jQuery) — defaults to identity for native DOM.
 *
 * @private
 * @param {(callback: (el: HTMLElement) => void) => void} register
 * @param {(el: HTMLElement) => any} [wrap]
 */
function deferredProxy(register, wrap = el => el) {
    const calls = [];
    const proxy = new Proxy({}, {
        get: (_, method) => (...args) => {
            calls.push([method, args]);
            return proxy;
        },
    });
    queueMicrotask(() => {
        register(el => {
            calls.forEach(([method, args]) => wrap(el)[method](...args));
        });
    });
    return proxy;
}

/**
 * Initializes the MutationObserver (only once).
 * @private
 */
function initObserver() {
    if (observer) return;

    observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                mounts.forEach((cb, sel) => {
                    if (node.matches(sel)) cb.call(node, node);
                    node.querySelectorAll(sel).forEach((el) => cb.call(el, el));
                });
            }
            for (const node of m.removedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                unmounts.forEach((cb, sel) => {
                    if (node.matches(sel)) cb.call(node, node);
                    node.querySelectorAll(sel).forEach((el) => cb.call(el, el));
                });
            }
        }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
}